import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  serviceOrderOperacionalService,
  ordemServicoService,
  veiculoService,
  colaboradorService
} from '../../services/api';

const TIPOS_SERVICO = [{ value: 'ESCOLAR', label: 'Escolar' }, { value: 'SAUDE', label: 'Saúde' }, { value: 'PARTICULAR', label: 'Particular' }];
const STATUS_OPCOES = [
  { value: '', label: 'Todos' },
  { value: 'ABERTA', label: 'Aberta' },
  { value: 'EM_ROTA', label: 'Em rota' },
  { value: 'FINALIZADA', label: 'Finalizada' },
  { value: 'CANCELADA', label: 'Cancelada' }
];

const DEFAULT_CHECKLIST = [
  'RETRATORES PARA CADEIRA DE RODA? (08 UNIDADES)',
  'MACACO MANUAL? (01 UNIDADE)',
  'CHAVE DE RODA? (01 UNIDADE)',
  'TRIANGULO DE SEGURANÇA? (01 UNIDADE)',
  'EXTENSOR DA CHAVE DE RODA? (01 UNIDADE)',
  'EXTINTOR DE INCÊNDIO? (01 UNIDADE)',
  'ESTEPE? (01 UNIDADE)',
  'ELEVADOR PNE FUNCIONANDO?',
  'PNEU - DIANTEIRO DIREITO?',
  'PNEU - DIANTEIRO ESQUERDO?',
  'PNEU - TRASEIRO DIREITO?',
  'PNEU - TRASEIRO ESQUERDO?',
  'ESTADO DAS RODAS?'
];

export default function OrdensServico() {
  const queryClient = useQueryClient();
  const [modalAberto, setModalAberto] = useState(false);
  const [osSelecionada, setOsSelecionada] = useState(null);
  const [filtros, setFiltros] = useState({ page: 1, limit: 10, status: '', dataInicio: '', dataFim: '' });
  const [importandoPdf, setImportandoPdf] = useState(false);
  const [modalMotoristaOpen, setModalMotoristaOpen] = useState(false);
  const [osParaMotorista, setOsParaMotorista] = useState(null);
  const [vinculandoMotorista, setVinculandoMotorista] = useState(false);
  const fileInputRef = React.useRef(null);
  const [formData, setFormData] = useState({
    vehicleId: '',
    driverId: '',
    data: new Date().toISOString().slice(0, 10),
    tipoServico: 'ESCOLAR',
    hodometroSaida: '',
    hodometroChegada: '',
    horarioSaida: '',
    horarioRecolhimento: '',
    observacoes: '',
    status: 'ABERTA',
    checklist: DEFAULT_CHECKLIST.map(item => ({ item, status: 'NA', observacao: '' })),
    passageiros: []
  });

  const filtrosEnvio = React.useMemo(() => {
    const f = { ...filtros };
    if (!f.status) delete f.status;
    if (!f.dataInicio) delete f.dataInicio;
    if (!f.dataFim) delete f.dataFim;
    return f;
  }, [filtros]);

  const { data: listagem, isLoading, refetch } = useQuery({
    queryKey: ['service-orders', filtrosEnvio],
    queryFn: () => serviceOrderOperacionalService.listar(filtrosEnvio),
    keepPreviousData: true
  });

  const { data: veiculosRes } = useQuery({
    queryKey: ['veiculos-ativos'],
    queryFn: () => veiculoService.listar({ ativo: true, limit: 500 })
  });

  const { data: colaboradoresRes } = useQuery({
    queryKey: ['colaboradores-motoristas'],
    queryFn: () => colaboradorService.listar({ ativo: 'true', limit: 500, incluirVeiculo: true })
  });

  const veiculos = Array.isArray(veiculosRes?.data?.data) ? veiculosRes.data.data : (veiculosRes?.data && !Array.isArray(veiculosRes.data) ? [] : (veiculosRes?.data || []));
  const colaboradores = Array.isArray(colaboradoresRes?.data?.data) ? colaboradoresRes.data.data : (colaboradoresRes?.data && !Array.isArray(colaboradoresRes.data) ? [] : (colaboradoresRes?.data || []));
  const motoristas = colaboradores.filter(c => (c.perfil || c.dadosTrabalho?.perfil || '').toLowerCase() === 'motorista' || c.documentos?.motorista);

  const resBody = listagem?.data;
  const lista = Array.isArray(resBody?.data) ? resBody.data : (resBody && !resBody.data ? [] : []);
  const pagination = resBody?.pagination ?? { page: 1, limit: 10, total: 0, pages: 1 };

  const veiculoSelecionado = veiculos.find(v => v._id === formData.vehicleId);
  const odometroAtual = veiculoSelecionado?.hodometro?.atual ?? veiculoSelecionado?.hodometro ?? '-';

  const createMutation = useMutation({
    mutationFn: serviceOrderOperacionalService.criar,
    onSuccess: () => {
      queryClient.invalidateQueries(['service-orders']);
      toast.success('OS criada com sucesso');
      fecharModal();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Erro ao criar OS')
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => serviceOrderOperacionalService.atualizar(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['service-orders']);
      toast.success('OS atualizada');
      fecharModal();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Erro ao atualizar')
  });

  const deleteMutation = useMutation({
    mutationFn: serviceOrderOperacionalService.deletar,
    onSuccess: () => {
      queryClient.invalidateQueries(['service-orders']);
      toast.success('OS excluída');
      fecharModal();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Erro ao excluir')
  });

  const pdfMutation = useMutation({
    mutationFn: serviceOrderOperacionalService.pdf,
    onSuccess: (response) => {
      const blob = response?.data;
      if (!blob || !(blob instanceof Blob)) {
        toast.error('Erro ao gerar PDF');
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `OS-${Date.now()}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF gerado');
    },
    onError: () => toast.error('Erro ao gerar PDF')
  });

  const abrirModal = async (os = null) => {
    setOsSelecionada(os);
    if (os) {
      let full = os;
      if (os._id && (!os.checklist || !os.passageiros)) {
        try {
          const res = await serviceOrderOperacionalService.buscar(os._id);
          full = res?.data ?? os;
        } catch (_) {}
      }
      setFormData({
        vehicleId: full.vehicleId?._id || full.vehicleId || '',
        driverId: full.driverId?._id || full.driverId || '',
        data: full.data ? new Date(full.data).toISOString().slice(0, 10) : formData.data,
        tipoServico: full.tipoServico || 'ESCOLAR',
        hodometroSaida: full.hodometroSaida ?? '',
        hodometroChegada: full.hodometroChegada ?? '',
        horarioSaida: full.horarioSaida || '',
        horarioRecolhimento: full.horarioRecolhimento || '',
        observacoes: full.observacoes || '',
        status: full.status || 'ABERTA',
        checklist: (full.checklist && full.checklist.length) ? full.checklist.map(c => ({ _id: c._id, item: c.item, status: c.status || 'NA', observacao: c.observacao || '' })) : formData.checklist,
        passageiros: Array.isArray(full.passageiros) ? full.passageiros.map(p => ({ ...p })) : []
      });
    } else {
      setFormData({
        vehicleId: '',
        driverId: '',
        data: new Date().toISOString().slice(0, 10),
        tipoServico: 'ESCOLAR',
        hodometroSaida: '',
        horarioSaida: '',
        horarioRecolhimento: '',
        observacoes: '',
        checklist: DEFAULT_CHECKLIST.map(item => ({ item, status: 'NA', observacao: '' })),
        passageiros: []
      });
    }
    setModalAberto(true);
  };

  const fecharModal = () => {
    setModalAberto(false);
    setOsSelecionada(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Ao selecionar motorista, preencher veiculo vinculado automaticamente
    if (name === 'driverId' && value) {
      const motoristaSel = motoristas.find(m => m._id === value);
      if (motoristaSel) {
        const veiculoVinculadoId =
          motoristaSel.veiculoVinculado?._id ||
          motoristaSel.veiculoVinculado ||
          motoristaSel.dadosTrabalho?.veiculoVinculado?._id ||
          motoristaSel.dadosTrabalho?.veiculoVinculado ||
          '';
        if (veiculoVinculadoId) {
          setFormData(prev => ({ ...prev, vehicleId: veiculoVinculadoId }));
          const veiculoInfo = veiculos.find(v => v._id === veiculoVinculadoId);
          if (veiculoInfo) {
            toast.success(`Veículo ${veiculoInfo.placa} - ${veiculoInfo.modelo} vinculado automaticamente!`);
          }
        }
      }
    }
  };

  const handleChecklistChange = (index, field, value) => {
    setFormData(prev => {
      const next = [...(prev.checklist || [])];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, checklist: next };
    });
  };

  // Importar dados de OS a partir de PDF do governo
  const importarPdf = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Selecione um arquivo PDF');
      return;
    }
    setImportandoPdf(true);
    try {
      const response = await ordemServicoService.importarPdf(file);
      const dados = response.data?.data;
      if (response.data?.success && dados) {
        // Preencher numero e data
        setFormData(prev => ({
          ...prev,
          numeroOs: dados.numero || prev.numeroOs,
          data: dados.data ? dados.data.split('/').reverse().join('-') : prev.data,
          passageiros: (dados.passageiros || []).map(p => ({
            nome: p.nome || '',
            cpf: p.cpf || '',
            acompanhante: p.acompanhante || '',
            telefone: p.telefone || '',
            enderecoEmbarque: p.enderecoEmbarque || '',
            horarioEmbarque: p.horarioEmbarque || '',
            horarioReembarque: p.horarioReembarque || '',
            horarioCompromisso: p.horarioCompromisso || '',
            kmEmbarque: p.kmEmbarque || '',
            enderecoDestino: p.enderecoDestino || '',
            horarioDestino: p.horarioDestino || '',
            kmDestino: p.kmDestino || '',
            numeroOsExterna: p.numeroOsExterna || ''
          }))
        }));
        toast.success(`${dados.passageiros?.length || 0} passageiros importados do PDF!`);
      } else {
        toast.error(response.data?.message || 'Não foi possível extrair dados do PDF');
      }
    } catch (error) {
      console.error('Erro ao importar PDF:', error);
      toast.error(error.response?.data?.message || 'Erro ao importar PDF');
    } finally {
      setImportandoPdf(false);
    }
  };

  // Importar multiplas OS de PDFs e criar automaticamente
  const importarMultiplosPdfs = async (files) => {
    setImportandoPdf(true);
    try {
      const response = await ordemServicoService.importarPdfs(files);
      const data = response.data;
      if (data?.success) {
        const criadas = data.data?.criadas || [];
        const erros = data.data?.erros || [];
        if (criadas.length > 0) {
          toast.success(`${criadas.length} OS importadas com sucesso!`);
        }
        if (erros.length > 0) {
          toast.error(`${erros.length} erro(s): ${erros.map(e => e.erro).join('; ')}`);
        }
        // Recarregar lista
        queryClient.invalidateQueries(['ordens-servico']);
        queryClient.invalidateQueries(['service-orders']);
        refetch();
      } else {
        toast.error(data?.message || 'Erro ao importar OS');
      }
    } catch (error) {
      console.error('Erro ao importar PDFs:', error);
      toast.error(error.response?.data?.message || 'Erro ao importar OS');
    } finally {
      setImportandoPdf(false);
    }
  };

  // Vincular motorista direto na listagem da OS
  const vincularMotoristaOS = async (motoristaId) => {
    if (!osParaMotorista) return;
    setVinculandoMotorista(true);
    try {
      // Buscar motorista para ver se tem veiculo vinculado
      const motoristaSel = motoristas.find(m => m._id === motoristaId);
      const veiculoVinculadoId =
        motoristaSel?.veiculoVinculado?._id ||
        motoristaSel?.veiculoVinculado ||
        motoristaSel?.dadosTrabalho?.veiculoVinculado?._id ||
        motoristaSel?.dadosTrabalho?.veiculoVinculado ||
        '';

      const dados = { driverId: motoristaId };
      if (veiculoVinculadoId) dados.vehicleId = veiculoVinculadoId;

      const response = await serviceOrderOperacionalService.atualizar(osParaMotorista._id, dados);
      if (response.data?.success || response.data?.data) {
        toast.success(
          veiculoVinculadoId
            ? 'Motorista vinculado! Veículo preenchido automaticamente.'
            : 'Motorista vinculado com sucesso!'
        );
        setModalMotoristaOpen(false);
        setOsParaMotorista(null);
        refetch();
      } else {
        toast.error(response.data?.message || 'Erro ao vincular motorista');
      }
    } catch (error) {
      console.error('Erro ao vincular motorista:', error);
      toast.error(error.response?.data?.message || 'Erro ao vincular motorista');
    } finally {
      setVinculandoMotorista(false);
    }
  };

  const addPassageiro = () => {
    setFormData(prev => ({
      ...prev,
      passageiros: [...(prev.passageiros || []), { nome: '', cpf: '', acompanhante: '', telefone: '', enderecoEmbarque: '', horarioEmbarque: '', horarioReembarque: '', horarioCompromisso: '', kmEmbarque: '', enderecoDestino: '', horarioDestino: '', kmDestino: '', numeroOsExterna: '' }]
    }));
  };

  const updatePassageiro = (index, field, value) => {
    setFormData(prev => {
      const next = [...(prev.passageiros || [])];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, passageiros: next };
    });
  };

  const removePassageiro = (index) => {
    setFormData(prev => ({ ...prev, passageiros: prev.passageiros.filter((_, i) => i !== index) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      vehicleId: formData.vehicleId,
      driverId: formData.driverId,
      data: formData.data,
      tipoServico: formData.tipoServico,
      hodometroSaida: formData.hodometroSaida ? Number(formData.hodometroSaida) : undefined,
      horarioSaida: formData.horarioSaida || undefined,
      horarioRecolhimento: formData.horarioRecolhimento || undefined,
      observacoes: formData.observacoes || undefined,
      passageiros: formData.passageiros?.filter(p => p.nome || p.cpf) || []
    };
    if (osSelecionada) {
      payload.hodometroChegada = formData.hodometroChegada ? Number(formData.hodometroChegada) : undefined;
      payload.status = formData.status || osSelecionada.status;
      updateMutation.mutate({ id: osSelecionada._id, data: payload });
      if (formData.checklist?.length) {
        try {
          await serviceOrderOperacionalService.atualizarChecklist(osSelecionada._id, formData.checklist.map(c => ({ _id: c._id, item: c.item, status: c.status, observacao: c.observacao })));
        } catch (_) {}
      }
    } else {
      payload.checklist = formData.checklist?.map(c => ({ item: c.item, status: c.status, observacao: c.observacao }));
      createMutation.mutate(payload);
    }
  };

  const isDfAcessivel = (veiculoSelecionado?.tipo || '').toLowerCase().includes('acessivel') || veiculoSelecionado?.tipoOperacional === 'DF_ACESSIVEL';

  return (
    <div className="p-6">
      <div className="bg-gradient-to-br from-indigo-950 via-indigo-900 to-purple-900 rounded-2xl p-6 md:p-8 text-white shadow-xl mb-6">
            <p className="text-indigo-300 text-sm font-medium uppercase tracking-widest mb-1">Frota</p>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Ordem de Serviço</h1>
            <p className="text-indigo-200 mt-1 text-sm">Controle de viagens e ordens de serviço</p>
          </div>

      <div className="bg-white rounded-lg shadow border border-neutral-200 overflow-hidden mb-4">
        <div className="px-4 py-3 border-b border-neutral-200 flex flex-wrap items-center gap-4">
          <select
            value={filtros.status}
            onChange={(e) => setFiltros(f => ({ ...f, status: e.target.value, page: 1 }))}
            className="border border-neutral-300 rounded px-2 py-1.5 text-sm"
          >
            {STATUS_OPCOES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <input
            type="date"
            value={filtros.dataInicio || ''}
            onChange={(e) => setFiltros(f => ({ ...f, dataInicio: e.target.value || undefined, page: 1 }))}
            className="border border-neutral-300 rounded px-2 py-1.5 text-sm"
            placeholder="Data início"
          />
          <input
            type="date"
            value={filtros.dataFim || ''}
            onChange={(e) => setFiltros(f => ({ ...f, dataFim: e.target.value || undefined, page: 1 }))}
            className="border border-neutral-300 rounded px-2 py-1.5 text-sm"
            placeholder="Data fim"
          />
          <button
            type="button"
            onClick={() => abrirModal()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Nova OS
          </button>
          <input
            type="file"
            accept="application/pdf"
            multiple
            className="hidden"
            id="importar-os-pdfs"
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (files.length > 0) importarMultiplosPdfs(files);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => document.getElementById('importar-os-pdfs')?.click()}
            disabled={importandoPdf}
            className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition"
          >
            {importandoPdf ? (
              <span className="animate-spin mr-2 inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full"></span>
            ) : (
              <span className="mr-2">📄</span>
            )}
            {importandoPdf ? 'Importando...' : 'Importar OS (PDF)'}
          </button>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-neutral-500">Carregando...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-neutral-600">Nº OS</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-neutral-600">Data</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-neutral-600">Veículo</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-neutral-600">Motorista</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-neutral-600">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-neutral-600">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200">
                {lista.map((os) => (
                  <tr key={os._id}>
                    <td className="px-4 py-2 text-sm">{os.numeroOs}</td>
                    <td className="px-4 py-2 text-sm">{os.data ? new Date(os.data).toLocaleDateString('pt-BR') : '-'}</td>
                    <td className="px-4 py-2 text-sm">
                      {os.vehicleId?.placa ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                          {os.vehicleId?.placa}
                        </span>
                      ) : (
                        <span className="text-neutral-400">Aguardando motorista</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm">
                      {os.driverId?.nome ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                          {os.driverId?.nome}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => { setOsParaMotorista(os); setModalMotoristaOpen(true); }}
                          className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 border border-indigo-100 transition"
                        >
                          + Vincular motorista
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm">{os.status}</td>
                    <td className="px-4 py-2 text-sm space-x-2">
                      <button type="button" onClick={() => abrirModal(os)} className="text-blue-600 hover:underline">Editar</button>
                      <button type="button" onClick={() => pdfMutation.mutate(os._id)} className="text-green-600 hover:underline">PDF</button>
                      {os.status !== 'FINALIZADA' && (
                        <button type="button" onClick={() => window.confirm('Excluir esta OS?') && deleteMutation.mutate(os._id)} className="text-red-600 hover:underline">Excluir</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination.pages > 1 && (
          <div className="px-4 py-2 border-t border-neutral-200 flex justify-between items-center">
            <span className="text-sm text-neutral-600">Total: {pagination.total}</span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={filtros.page <= 1}
                onClick={() => setFiltros(f => ({ ...f, page: f.page - 1 }))}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Anterior
              </button>
              <span className="py-1 text-sm">Página {filtros.page} de {pagination.pages}</span>
              <button
                type="button"
                disabled={filtros.page >= pagination.pages}
                onClick={() => setFiltros(f => ({ ...f, page: f.page + 1 }))}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>

      {modalAberto && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 flex items-start justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full my-8 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b flex justify-between items-center bg-gradient-to-r from-indigo-950 to-indigo-900 rounded-t-lg">
              <div>
                <h2 className="text-xl font-bold text-white">{osSelecionada ? `OS #${osSelecionada.numeroOs}` : 'Nova OS Operacional'}</h2>
                <p className="text-xs text-indigo-300 mt-0.5">Preencha manualmente ou importe do PDF do governo</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="application/pdf"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files?.[0]) importarPdf(e.target.files[0]);
                    e.target.value = '';
                  }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importandoPdf}
                  className="inline-flex items-center px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm font-medium rounded-xl transition disabled:opacity-50"
                >
                  {importandoPdf ? (
                    <span className="animate-spin mr-2 inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full"></span>
                  ) : (
                    <span className="mr-2">📄</span>
                  )}
                  {importandoPdf ? 'Importando...' : 'Importar OS do PDF'}
                </button>
                <button type="button" onClick={fecharModal} className="text-white hover:text-gray-300 text-xl font-bold">✕</button>
              </div>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Veículo</label>
                  <select name="vehicleId" value={formData.vehicleId} onChange={handleChange} className="w-full border border-neutral-300 rounded px-3 py-2">
                    <option value="">Selecione (ou vincula com o motorista)</option>
                    {veiculos.filter(v => v.ativo !== false).map(v => (
                      <option key={v._id} value={v._id}>{v.placa} - {v.modelo}</option>
                    ))}
                  </select>
                  {formData.vehicleId && (
                    <p className="mt-1 text-xs text-neutral-500">Odômetro atual: {odometroAtual} km</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Motorista</label>
                  <select name="driverId" value={formData.driverId} onChange={handleChange} className="w-full border border-neutral-300 rounded px-3 py-2">
                    <option value="">Selecione (ou importe do PDF)</option>
                    {motoristas.map(m => (
                      <option key={m._id} value={m._id}>{m.nome}</option>
                    ))}
                  </select>
                  {formData.driverId && (
                    <p className="mt-1 text-xs text-neutral-500">
                      Motorista selecionado. Veículo vinculado automaticamente abaixo.
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Data</label>
                  <input type="date" name="data" value={formData.data} onChange={handleChange} className="w-full border border-neutral-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Tipo de serviço</label>
                  <select name="tipoServico" value={formData.tipoServico} onChange={handleChange} className="w-full border border-neutral-300 rounded px-3 py-2">
                    {TIPOS_SERVICO.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Hodômetro saída</label>
                  <input type="number" name="hodometroSaida" value={formData.hodometroSaida} onChange={handleChange} min="0" className="w-full border border-neutral-300 rounded px-3 py-2" placeholder="km" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Horário saída</label>
                  <input type="time" name="horarioSaida" value={formData.horarioSaida} onChange={handleChange} className="w-full border border-neutral-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Horário recolhimento</label>
                  <input type="time" name="horarioRecolhimento" value={formData.horarioRecolhimento} onChange={handleChange} className="w-full border border-neutral-300 rounded px-3 py-2" />
                </div>
              </div>

              {osSelecionada && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Status</label>
                      <select name="status" value={formData.status} onChange={handleChange} className="w-full border border-neutral-300 rounded px-3 py-2">
                        <option value="ABERTA">Aberta</option>
                        <option value="EM_ROTA">Em rota</option>
                        <option value="FINALIZADA">Finalizada</option>
                        <option value="CANCELADA">Cancelada</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Hodômetro chegada (obrigatório ao finalizar)</label>
                      <input type="number" name="hodometroChegada" value={formData.hodometroChegada} onChange={handleChange} min="0" className="w-full border border-neutral-300 rounded px-3 py-2" placeholder="km" />
                    </div>
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">Checklist</label>
                <div className="space-y-2 border rounded p-3 bg-neutral-50">
                  {(formData.checklist || []).map((c, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-2">
                      <span className="text-sm w-48 truncate">{c.item}</span>
                      <select value={c.status} onChange={(e) => handleChecklistChange(i, 'status', e.target.value)} className="border rounded px-2 py-1 text-sm">
                        <option value="NA">N/A</option>
                        <option value="SIM">Sim</option>
                        <option value="NAO">Não</option>
                      </select>
                      <input type="text" value={c.observacao || ''} onChange={(e) => handleChecklistChange(i, 'observacao', e.target.value)} placeholder="Obs. se NÃO" className="flex-1 min-w-0 border rounded px-2 py-1 text-sm" />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-neutral-700">Passageiros</label>
                  <button type="button" onClick={addPassageiro} className="text-sm text-blue-600 hover:underline">+ Adicionar</button>
                </div>
                <div className="space-y-3 border rounded p-3 bg-neutral-50">
                  {(formData.passageiros || []).map((p, i) => (
                    <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-2 p-2 border rounded bg-white">
                      <input placeholder="Nome" value={p.nome || ''} onChange={(e) => updatePassageiro(i, 'nome', e.target.value)} className="border rounded px-2 py-1 text-sm" />
                      <input placeholder="CPF" value={p.cpf || ''} onChange={(e) => updatePassageiro(i, 'cpf', e.target.value)} className="border rounded px-2 py-1 text-sm" />
                      <input placeholder="Acompanhante" value={p.acompanhante || ''} onChange={(e) => updatePassageiro(i, 'acompanhante', e.target.value)} className="border rounded px-2 py-1 text-sm" />
                      <input placeholder="Telefone" value={p.telefone || ''} onChange={(e) => updatePassageiro(i, 'telefone', e.target.value)} className="border rounded px-2 py-1 text-sm" />
                      <input placeholder="End. embarque" value={p.enderecoEmbarque || ''} onChange={(e) => updatePassageiro(i, 'enderecoEmbarque', e.target.value)} className="border rounded px-2 py-1 text-sm md:col-span-2" />
                      <input placeholder="Horário embarque" type="time" value={p.horarioEmbarque || ''} onChange={(e) => updatePassageiro(i, 'horarioEmbarque', e.target.value)} className="border rounded px-2 py-1 text-sm" />
                      <input placeholder="Horário compromisso" type="time" value={p.horarioCompromisso || ''} onChange={(e) => updatePassageiro(i, 'horarioCompromisso', e.target.value)} className="border rounded px-2 py-1 text-sm" />
                      <input placeholder="End. destino" value={p.enderecoDestino || ''} onChange={(e) => updatePassageiro(i, 'enderecoDestino', e.target.value)} className="border rounded px-2 py-1 text-sm" />
                      <input placeholder="Horário reembarque" type="time" value={p.horarioReembarque || ''} onChange={(e) => updatePassageiro(i, 'horarioReembarque', e.target.value)} className="border rounded px-2 py-1 text-sm" />
                      <input placeholder="Km embarque" type="number" value={p.kmEmbarque ?? ''} onChange={(e) => updatePassageiro(i, 'kmEmbarque', e.target.value)} className="border rounded px-2 py-1 text-sm" />
                      <input placeholder="Km destino" type="number" value={p.kmDestino ?? ''} onChange={(e) => updatePassageiro(i, 'kmDestino', e.target.value)} className="border rounded px-2 py-1 text-sm" />
                      {isDfAcessivel && (
                        <input placeholder="Nº OS externa *" value={p.numeroOsExterna || ''} onChange={(e) => updatePassageiro(i, 'numeroOsExterna', e.target.value)} className="border rounded px-2 py-1 text-sm md:col-span-2" />
                      )}
                      <button type="button" onClick={() => removePassageiro(i)} className="text-red-600 text-sm md:col-span-2">Remover</button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Observações</label>
                <textarea name="observacoes" value={formData.observacoes || ''} onChange={handleChange} rows={2} className="w-full border border-neutral-300 rounded px-3 py-2" />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <button type="button" onClick={fecharModal} className="px-4 py-2 border rounded">Cancelar</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                  {osSelecionada ? 'Salvar' : 'Criar OS'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Vincular Motorista na OS */}
      {modalMotoristaOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto"
             onClick={() => setModalMotoristaOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg my-8"
               onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-indigo-950 to-indigo-900 rounded-t-2xl">
              <div>
                <h3 className="text-lg font-bold text-white">Vincular Motorista</h3>
                <p className="text-sm text-indigo-300">
                  OS #{osParaMotorista?.numeroOs} · {osParaMotorista?.passageiros?.length || 0} passageiros
                </p>
              </div>
              <button onClick={() => setModalMotoristaOpen(false)} className="text-white hover:text-gray-300 text-2xl font-bold">×</button>
            </div>

            <div className="p-6">
              {motoristas.length === 0 ? (
                <div className="py-10 text-center">
                  <p className="text-gray-500">Nenhum motorista disponível</p>
                  <p className="text-sm text-gray-400 mt-1">Cadastre colaboradores com perfil motorista</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {motoristas.map(m => {
                    const veiculoId = m.veiculoVinculado?._id || m.veiculoVinculado || m.dadosTrabalho?.veiculoVinculado?._id || m.dadosTrabalho?.veiculoVinculado || '';
                    const veiculoInfo = veiculoId ? veiculos.find(v => v._id === veiculoId) : null;
                    return (
                      <div key={m._id} className="flex items-center justify-between p-3 border border-gray-200 rounded-xl hover:bg-indigo-50/50 transition">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
                            {m.nome?.split(' ').map(p => p[0]).slice(0, 2).join('')}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{m.nome}</p>
                            <p className="text-xs text-gray-500">
                              {m.matricula || ''}
                              {veiculoInfo ? ` · Veículo: ${veiculoInfo.placa} - ${veiculoInfo.modelo}` : ' · Sem veículo vinculado'}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => vincularMotoristaOS(m._id)}
                          disabled={vinculandoMotorista}
                          className="px-4 py-2 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition"
                        >
                          {vinculandoMotorista ? 'Vinculando...' : 'Vincular'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
